import { TFunction } from 'i18next';
import moment, { Moment } from 'moment';
import {
	FilterBar2,
	FilterBar2ListItem,
	FilterBar2FilterType,
	DateRangeKeyword,
	type PresetKeyRanges
} from '@bringg/react-components';
import { Option } from '@bringg/react-components/dist/components/filter-bar';
import { CustomAttributeDataType, FILTER_CONDITIONS, Team as BringgTeam } from '@bringg/types';
import { copyProperty } from '@bringg-frontend/utils';

import SkillsStore from 'bringg-web/stores/skills/skills-store';
import TeamsStore from 'bringg-web/stores/teams/teams-store';
import MerchantConfigurationsStore from 'bringg-web/stores/merchant-configurations/merchant-configurations-store';
import { mapItemsToFilter, mapTeamToFilter } from './fliter-bar-utils';
import CustomAttributesStore from 'bringg-web/stores/custom-attributes/custom-attributes-store';

export interface FilterObject {
	plannedTask?: number;
	selectedTags?: number[];
	selectedTeams?: number[];
	timeWindow?: string;
	searchString: string;
	timeWindowStart?: Moment;
	timeWindowEnd?: Moment;
	firstCustomer?: string;
	lastCustomer?: string;
	firstAddress?: string;
	lastAddress?: string;
	unassigned?: number;
	firstCity?: string;
	lastCity?: string;
	autoDispatch?: number[];
	skills?: number[];
	ranks: number[];
	fleets: number[];
	slaStartTime?: Moment;
	slaEndTime?: Moment;
	servicePlans: number[];
	unscheduled: number;
	dates?: Moment[];
	sla?: Moment[];
	addedFilters: string[];
	reservedUntil?: Moment[];
	reservedUntilStartTime?: Moment;
	reservedUntilEndTime?: Moment;
	availableCustomAttributesFilterTypes?: CustomAttributesFilterTypes;
}

export interface MultipleSelectState {
	options: Option<number>[];
	value: Option<number>[];
	maxItems?: number;
}

export const multiSelectTranslation = (t: TFunction) => ({
	clearSelectionText: t('FILTER_BAR.CLEAR'),
	selectAllText: t('FILTER_BAR.SELECT_ALL'),
	searchItemPlaceholder: t('FILTER_BAR.SEARCH')
});

export const teamsTranslation = (t: TFunction) => ({
	unknownItemText: t('FILTER_BAR.UNKNOWN_TEAM'),
	multiselectPlaceholder: t('FILTER_BAR.TEAMS_TEXT'),
	oneItemSelected: t('FILTER_BAR.TEAM_SELECTED'),
	multipleItemsSelected: t('FILTER_BAR.TEAMS_SELECTED'),
	allItemsSelected: t('FILTER_BAR.ALL_TEAMS_SELECTED'),
	...multiSelectTranslation(t)
});

export const tagsTranslation = (t: TFunction) => ({
	unknownItemText: t('FILTER_BAR.UNKNOWN_TAG'),
	multiselectPlaceholder: t('FILTER_BAR.TAGS_TEXT'),
	oneItemSelected: t('FILTER_BAR.TAG_SELECTED'),
	multipleItemsSelected: t('FILTER_BAR.TAGS_SELECTED'),
	allItemsSelected: t('FILTER_BAR.ALL_TAGS_SELECTED'),
	...multiSelectTranslation(t)
});

export const autoDispatchTranslation = (t: TFunction) => ({
	unknownItemText: t('FILTER_BAR.UNKNOWN_AD_STATUS'),
	multiselectPlaceholder: t('DISPATCH_LIST.AUTO_DISPATCH'),
	oneItemSelected: t('FILTER_BAR.AD_STATUS_SELECTED'),
	multipleItemsSelected: t('FILTER_BAR.AD_STATUSES_SELECTED'),
	allItemsSelected: t('FILTER_BAR.ALL_AD_STATUS_SELECTED'),
	...multiSelectTranslation(t)
});

export const skillsTranslation = (t: TFunction) => ({
	unknownItemText: t('FILTER_BAR.UNKNOWN_SKILL'),
	multiselectPlaceholder: t('DISPATCH_LIST.SKILLS'),
	oneItemSelected: t('FILTER_BAR.SKILL_SELECTED'),
	multipleItemsSelected: t('FILTER_BAR.SKILLS_SELECTED'),
	allItemsSelected: t('FILTER_BAR.ALL_SKILLS_SELECTED'),
	...multiSelectTranslation(t)
});

export const rangeDateTranslation = (t: TFunction) => ({
	today: t('FILTER_BAR.TODAY'),
	tomorrow: t('FILTER_BAR.TOMORROW'),
	nextTwoDays: t('FILTER_BAR.NEXT_TWO_DAYS'),
	last7Days: t('FILTER_BAR.LAST_7_DAYS'),
	last30Days: t('FILTER_BAR.LAST_30_DAYS'),
	thisMonth: t('FILTER_BAR.THIS_MONTH'),
	lastMonth: t('FILTER_BAR.LAST_MONTH'),
	overdueOrders: t('FILTER_BAR.OVERDUE_ORDERS'),
	fromDate: t('FILTER_BAR.FROM_DATE'),
	toDate: t('FILTER_BAR.TO_DATE')
});

export const slaRangeTranslation = (t: TFunction) => ({
	today: t('FILTER_BAR.TODAY'),
	todayAndTomorrow: t('FILTER_BAR.TODAY_AND_TOMORROW'),
	thisWeek: t('FILTER_BAR.THIS_WEEK'),
	fromDate: t('FILTER_BAR.FROM_DATE'),
	toDate: t('FILTER_BAR.TO_DATE')
});

export const rankTranslation = (t: TFunction) => ({
	unknownItemText: t('FILTER_BAR.UNKNOWN_RANK'),
	multiselectPlaceholder: t('FILTER_BAR.RANKS'),
	oneItemSelected: t('FILTER_BAR.RANK_SELECTED'),
	multipleItemsSelected: t('FILTER_BAR.RANKS_SELECTED'),
	allItemsSelected: t('FILTER_BAR.ALL_RANKS_SELECTED'),
	...multiSelectTranslation(t)
});

export const fleetsTranslation = (t: TFunction) => ({
	unknownItemText: t('FILTER_BAR.UNKNOWN_FLEET'),
	multiselectPlaceholder: t('FILTER_BAR.FLEETS'),
	oneItemSelected: t('FILTER_BAR.FLEET_SELECTED'),
	multipleItemsSelected: t('FILTER_BAR.FLEETS_SELECTED'),
	allItemsSelected: t('FILTER_BAR.ALL_FLEETS_SELECTED'),
	...multiSelectTranslation(t)
});

export const ServicePlansTranslation = (t: TFunction) => ({
	unknownItemText: t('FILTER_BAR.UNKNOWN_SERVICE_PLANS'),
	multiselectPlaceholder: t('FILTER_BAR.SERVICE_PLANS'),
	oneItemSelected: t('FILTER_BAR.SERVICE_PLAN_SELECTED'),
	multipleItemsSelected: t('FILTER_BAR.SERVICE_PLANS_SELECTED'),
	allItemsSelected: t('FILTER_BAR.ALL_SERVICE_PLANS_SELECTED'),
	...multiSelectTranslation(t)
});

export const unassignedOption = (t: TFunction) => [
	{ value: 2, label: t('FILTER_BAR.SELECT_ASSIGN_OPTION') },
	{ value: 1, label: t('FILTER_BAR.ASSIGNED_TASKS') },
	{ value: 0, label: t('FILTER_BAR.UNASSIGNED_TASKS') }
];

export const plannedTaskOption = (t: TFunction) => [
	{ value: 2, label: t('FILTER_BAR.ALL_ORDERS') },
	{ value: 1, label: t('FILTER_BAR.PLANNED_TASKS') },
	{ value: 0, label: t('FILTER_BAR.UNPLANNED_TASKS') }
];

export const autoDispatchOption = (t: TFunction) => [
	{ value: 1, label: t('AUTO_DISPATCH_STATE.TASK.IN_PROCESS') },
	{ value: 2, label: t('AUTO_DISPATCH_STATE.TASK.DISPATCHED') },
	{ value: 3, label: t('AUTO_DISPATCH_STATE.TASK.FAILED') },
	{ value: 4, label: t('AUTO_DISPATCH_STATE.TASK.NOT_APPLIED') }
];

export const unscheduledOption = (t: TFunction) => [
	{ value: 0, label: t('FILTER_BAR.ALL_ORDERS') },
	{ value: 1, label: t('FILTER_BAR.UNSCHEDULED_ORDERS') }
];

export const readyForPickupOption = (t: TFunction) => [
	{ value: 0, label: t('FILTER_BAR.ALL_ORDERS') },
	{ value: 2, label: t('FILTER_BAR.READY_FOR_PICKUP_OPTION') },
	{ value: 1, label: t('FILTER_BAR.NOT_READY_FOR_PICKUP_OPTION') }
];

export const filterBar2Translations = (t: TFunction) => ({
	moreFilters: t('FILTER_BAR.ADD_FILTERS'),
	reset: t('FILTER_BAR.RESET'),
	done: t('FILTER_BAR.DONE'),
	cancel: t('FILTER_BAR.CANCEL'),
	search: t('FILTER_BAR.SEARCH'),
	all: t('FILTER_BAR.ALL'),
	selectAll: t('FILTER_BAR.SELECT_ALL'),
	specifiedPeriod: t('FILTER_BAR.SPECIFIED_PERIOD'),
	preset: t('FILTER_BAR.PRESET'),
	teams: t('FILTER_BAR.PARENT_TEAMS'),
	subTeams: t('FILTER_BAR.TEAMS'),
	conditionFilter: {
		operatorSelectPlaceholder: t('FILTER_BAR.CONDITION_FILTER.PLACEHOLDER'),
		options: {
			[FILTER_CONDITIONS.EQUALS]: t('FILTER_BAR.CONDITION_FILTER.EQUALS'),
			[FILTER_CONDITIONS.NOT_EQUALS]: t('FILTER_BAR.CONDITION_FILTER.NOT_EQUALS'),
			[FILTER_CONDITIONS.IS_EMPTY]: t('FILTER_BAR.CONDITION_FILTER.IS_EMPTY'),
			[FILTER_CONDITIONS.IS_NOT_EMPTY]: t('FILTER_BAR.CONDITION_FILTER.IS_NOT_EMPTY'),
			[FILTER_CONDITIONS.GREATER_THAN]: '>',
			[FILTER_CONDITIONS.GREATER_THAN_OR_EQUALS]: '≥',
			[FILTER_CONDITIONS.LESS_THAN]: '<',
			[FILTER_CONDITIONS.LESS_THAN_OR_EQUALS]: '≤'
		}
	}
});

export const filterBarPlaceholderTranslations = (t: TFunction) => ({
	unassigned: t('FILTER_BAR.UNASSIGNED'),
	planning: t('FILTER_BAR.PLANNING'),
	dates: t('FILTER_BAR.DATES')
});

export const rankOptions = Array.from(Array(10), (_, i) => ({ id: i + 1, name: `${i + 1}` }));

export const filterBarMoreFiltersInputs = [
	{ value: 'firstCustomer', label: 'WAY_POINTS_FIRST_CUSTOMER_NAME' },
	{ value: 'lastCustomer', label: 'WAY_POINTS_LAST_CUSTOMER_NAME' },
	{ value: 'firstAddress', label: 'WAY_POINTS_FIRST_ADDRESS' },
	{ value: 'lastAddress', label: 'WAY_POINTS_LAST_ADDRESS' },
	{ value: 'firstCity', label: 'WAY_POINTS_FIRST_CITY' },
	{ value: 'lastCity', label: 'WAY_POINTS_LAST_CITY' }
];

export const defaultDateTimePickerRanges = (t: TFunction) => {
	const translations = rangeDateTranslation(t);

	return {
		[translations.today]: [moment().startOf('day'), moment().endOf('day')],
		[translations.tomorrow]: [moment().add(1, 'd').startOf('day'), moment().add(1, 'd').endOf('day')],
		[translations.nextTwoDays]: [moment().add(1, 'd').startOf('day'), moment().add(2, 'd').endOf('day')],
		[translations.overdueOrders]: [moment().subtract(1, 'year'), moment()]
	} as { [name: string]: [moment.Moment, moment.Moment] };
};

export const defaultDateTimeDynamicRanges = (t: TFunction): PresetKeyRanges => {
	const translations = rangeDateTranslation(t);

	return {
		[DateRangeKeyword.Today]: {
			translatedLabel: translations.today
		},
		[DateRangeKeyword.Tomorrow]: {
			translatedLabel: translations.tomorrow
		},
		[DateRangeKeyword.NextTwoDays]: {
			translatedLabel: translations.nextTwoDays
		},
		[DateRangeKeyword.LateOrders]: {
			translatedLabel: translations.overdueOrders
		}
	};
};

export const MAX_TEAMS_IN_FILTER = 5;
export const OPTION_STATUS = 2;
export const NO_TIME_FILTER = 'ALL';
export const NO_TIME_FILTER_LABEL = 'ORDERS_TIME_FILTER.SHOW_ALL';
export const FILTERS_DEFAULT_VALUES = {
	selectedTags: [],
	selectedTeams: [],
	timeWindowStart: undefined,
	timeWindowEnd: undefined,
	firstAddress: '',
	lastAddress: '',
	firstCustomer: '',
	lastCustomer: '',
	unassigned: OPTION_STATUS,
	firstCity: '',
	lastCity: '',
	autoDispatch: [],
	skills: [],
	searchString: '',
	plannedTask: OPTION_STATUS,
	ranks: [],
	fleets: [],
	slaStartTime: undefined,
	slaEndTime: undefined,
	servicePlans: [],
	unscheduled: 0,
	readyForPickup: 0,
	addedFilters: [],
	dates: [],
	sla: [],
	reservedUntil: [],
	reservedUntilStartTime: undefined,
	reservedUntilEndTime: undefined,
	availableCustomAttributesFilterTypes: {},
	driversServiceArea: []
};

export const timeDefaultValue = [moment('00:00:00', 'HH:mm'), moment('23:59:59', 'HH:mm:ss')];

// ATTENTION: the same var with the same value used in apps/bringg-web/src/app/scripts/services/custom-attributes/custom-attributes-service.js
// this vars should have same value as they used are creating filter item key
export const customAttributeFilterNameKey = 'custom-attribute-';

export type ViewNames = '/planning' | '/map' | '/list';

export type CustomAttributesFilterTypes = Record<string, CustomAttributeDataType>;

export const getFilterList = (
	fleetsList: MultipleSelectState,
	isPlanning: () => boolean,
	skillsStore: SkillsStore,
	tags: MultipleSelectState,
	teamsStore: TeamsStore,
	teams: MultipleSelectState,
	servicePlansList: MultipleSelectState,
	merchantConfigurationsStore: MerchantConfigurationsStore,
	hourFormat: string,
	dateFormat: string,
	t: TFunction,
	customAttributesStore: CustomAttributesStore,
	isCustomAttributesFiltersAvailable: boolean,
	isDateTimeRangeDynamicRangesEnabled: boolean,
	serviceAreas: MultipleSelectState
): { defaultList: FilterBar2ListItem[]; additionals: FilterBar2ListItem[] } => {
	// Type casting were used because of union types issues
	const { rangePickerType, ranges } = (
		isDateTimeRangeDynamicRangesEnabled
			? {
					rangePickerType: FilterBar2FilterType.DATE_RANGE_V2,
					ranges: defaultDateTimeDynamicRanges(t)
			  }
			: {
					rangePickerType: FilterBar2FilterType.DATE_RANGE,
					ranges: defaultDateTimePickerRanges(t)
			  }
	) as { rangePickerType: FilterBar2FilterType.DATE_RANGE_V2; ranges: PresetKeyRanges };

	const defaultList: FilterBar2ListItem[] = [
		{
			type: FilterBar2.TYPES.SEARCH,
			name: 'searchString',
			title: t('TASK.SEARCH'),
			placeholder: t('TASK.SEARCH')
		},
		{
			type: rangePickerType,
			name: 'dates',
			title: filterBarPlaceholderTranslations(t).dates,
			tooltip: t('FILTER_BAR.TOOLTIP.DATES'),
			customProps: {
				showTime: { format: hourFormat, defaultValue: [moment().startOf('day'), moment().endOf('day')] },
				format: `${dateFormat} ${hourFormat}`,
				ranges,
				placeholder: [rangeDateTranslation(t).fromDate, rangeDateTranslation(t).toDate]
			},
			applyFilterOnValueChange: true
		}
	];
	if (teams.options?.length)
		defaultList.push({
			type: FilterBar2.TYPES.TEAMS,
			name: 'selectedTeams',
			title: teamsTranslation(t).multiselectPlaceholder,
			placeholder: teamsTranslation(t).multiselectPlaceholder,
			tooltip: t('FILTER_BAR.TOOLTIP.TEAMS'),
			values: mapTeamToFilter(teams.options as (BringgTeam & { childrenTeamIds: number[] })[], teamsStore),
			multiselect: true,
			additional: false,
			applyFilterOnValueChange: true
		});

	const additionals: FilterBar2ListItem[] = [];

	if (isPlanning())
		additionals.push({
			type: FilterBar2.TYPES.RADIO,
			name: 'plannedTask',
			title: filterBarPlaceholderTranslations(t).planning,
			placeholder: filterBarPlaceholderTranslations(t).planning,
			tooltip: t('FILTER_BAR.TOOLTIP.PLANNED_TASKS'),
			additional: true,
			values: plannedTaskOption(t),
			applyFilterOnValueChange: true
		});
	if (tags.options?.length)
		additionals.push({
			type: FilterBar2.TYPES.SELECT,
			name: 'selectedTags',
			title: tagsTranslation(t).multiselectPlaceholder,
			placeholder: tagsTranslation(t).multiselectPlaceholder,
			tooltip: t('FILTER_BAR.TOOLTIP.TAGS'),
			multiselect: true,
			additional: true,
			values: mapItemsToFilter(copyProperty(tags.options, 'tag', 'name')),
			applyFilterOnValueChange: true
		});
	additionals.push({
		type: FilterBar2.TYPES.RADIO,
		name: 'unassigned',
		title: filterBarPlaceholderTranslations(t).unassigned,
		placeholder: filterBarPlaceholderTranslations(t).unassigned,
		tooltip: t('FILTER_BAR.TOOLTIP.UNASSIGNED'),
		additional: true,
		values: unassignedOption(t),
		applyFilterOnValueChange: true
	});
	additionals.push(
		...filterBarMoreFiltersInputs.map(filter => ({
			type: FilterBar2.TYPES.DROPDOWN_SEARCH,
			name: filter.value,
			title: t(`DISPATCH_LIST.${filter.label}`),
			placeholder: t(`DISPATCH_LIST.${filter.label}`),
			tooltip: t(`FILTER_BAR.TOOLTIP.${filter.label}`),
			additional: true,
			applyFilterOnValueChange: true
		}))
	);
	if (merchantConfigurationsStore?.autoDispatchStatus && !isPlanning())
		additionals.push({
			type: FilterBar2.TYPES.CHECKBOX,
			name: 'autoDispatch',
			values: autoDispatchOption(t),
			title: autoDispatchTranslation(t).multiselectPlaceholder,
			placeholder: autoDispatchTranslation(t).multiselectPlaceholder,
			tooltip: t('FILTER_BAR.TOOLTIP.AD_STATUS'),
			additional: true,
			applyFilterOnValueChange: true
		});
	additionals.push({
		type: rangePickerType,
		name: 'sla',
		title: t('FILTER_BAR.SLA_WINDOW'),
		tooltip: t('FILTER_BAR.TOOLTIP.SLA'),
		customProps: {
			showTime: { format: hourFormat },
			format: `${dateFormat} ${hourFormat}`,
			ranges: ranges,
			placeholder: [rangeDateTranslation(t).fromDate, rangeDateTranslation(t).toDate]
		},
		additional: true,
		applyFilterOnValueChange: true
	});
	if (merchantConfigurationsStore?.enableSkills && skillsStore?.all)
		additionals.push({
			type: FilterBar2.TYPES.SELECT,
			name: 'skills',
			title: skillsTranslation(t).multiselectPlaceholder,
			placeholder: skillsTranslation(t).multiselectPlaceholder,
			tooltip: t('FILTER_BAR.TOOLTIP.SKILLS'),
			multiselect: true,
			additional: true,
			values: mapItemsToFilter(skillsStore?.all),
			applyFilterOnValueChange: true
		});
	if (fleetsList?.options?.length)
		additionals.push({
			type: FilterBar2.TYPES.SELECT,
			name: 'fleets',
			title: fleetsTranslation(t).multiselectPlaceholder,
			placeholder: fleetsTranslation(t).multiselectPlaceholder,
			tooltip: t('FILTER_BAR.TOOLTIP.FLEETS'),
			multiselect: true,
			additional: true,
			values: mapItemsToFilter(fleetsList.options),
			applyFilterOnValueChange: true
		});
	additionals.push({
		type: FilterBar2.TYPES.SELECT,
		name: 'ranks',
		title: rankTranslation(t).multiselectPlaceholder,
		placeholder: rankTranslation(t).multiselectPlaceholder,
		tooltip: t('FILTER_BAR.TOOLTIP.RANKS'),
		multiselect: true,
		additional: true,
		values: mapItemsToFilter(rankOptions),
		applyFilterOnValueChange: true
	});
	additionals.push({
		type: FilterBar2.TYPES.RADIO,
		name: 'unscheduled',
		title: t('FILTER_BAR.UNSCHEDULED_ORDERS'),
		placeholder: t('FILTER_BAR.UNSCHEDULED_ORDERS'),
		tooltip: t('FILTER_BAR.UNSCHEDULED_ORDERS_TOOLTIP'),
		additional: true,
		values: unscheduledOption(t),
		applyFilterOnValueChange: true
	});
	additionals.push({
		type: FilterBar2.TYPES.RADIO,
		name: 'readyForPickup',
		title: t('FILTER_BAR.READY_FOR_PICKUP'),
		placeholder: t('FILTER_BAR.READY_FOR_PICKUP'),
		tooltip: t('FILTER_BAR.READY_FOR_PICKUP_TOOLTIP'),
		additional: true,
		values: readyForPickupOption(t),
		applyFilterOnValueChange: true
	});
	if (servicePlansList?.options?.length) {
		additionals.push({
			type: FilterBar2.TYPES.SELECT,
			name: 'servicePlans',
			title: ServicePlansTranslation(t).multiselectPlaceholder,
			placeholder: ServicePlansTranslation(t).multiselectPlaceholder,
			tooltip: t('FILTER_BAR.TOOLTIP.SERVICE_PLANS'),
			additional: true,
			multiselect: true,
			values: mapItemsToFilter(servicePlansList.options),
			applyFilterOnValueChange: true
		});
	}
	additionals.push({
		type: rangePickerType,
		name: 'reservedUntil',
		additional: true,
		title: t('FILTER_BAR.RESERVED_UNTIL'),
		tooltip: t('FILTER_BAR.TOOLTIP.RESERVED_UNTIL'),
		customProps: {
			showTime: { format: hourFormat, defaultValue: [moment().startOf('day'), moment().endOf('day')] },
			format: `${dateFormat} ${hourFormat}`,
			ranges: ranges,
			placeholder: [rangeDateTranslation(t).fromDate, rangeDateTranslation(t).toDate]
		},
		applyFilterOnValueChange: true
	});

	if (serviceAreas?.options?.length) {
		additionals.push({
			type: FilterBar2.TYPES.SELECT,
			name: 'driversServiceArea',
			title: t('FILTER_BAR.DRIVERS_SERVICE_AREA'),
			placeholder: t('FILTER_BAR.DRIVERS_SERVICE_AREA_PLACEHOLDER'),
			tooltip: t('FILTER_BAR.DRIVERS_SERVICE_AREA_TOOLTIP'),
			multiselect: true,
			additional: true,
			values: mapItemsToFilter(serviceAreas.options),
			applyFilterOnValueChange: true
		});
	}

	// system filters should be always before CA filters
	if (isCustomAttributesFiltersAvailable && customAttributesStore.activeCustomAttributes.length) {
		customAttributesStore.activeCustomAttributes.forEach(item => {
			const filterName = `${customAttributeFilterNameKey}${item.id}`;
			switch (item.data_type) {
				case CustomAttributeDataType.String:
					additionals.push({
						type: FilterBar2.TYPES.DROPDOWN_SEARCH,
						name: filterName,
						additional: true,
						title: item.name,
						applyFilterOnValueChange: true
					});
					break;

				case CustomAttributeDataType.Boolean:
					additionals.push({
						type: FilterBar2.TYPES.RADIO,
						name: filterName,
						additional: true,
						title: item.name,
						applyFilterOnValueChange: true,
						values: [
							{ value: 1, label: t('GLOBAL.NO') },
							{ value: 2, label: t('GLOBAL.YES') }
						]
					});
					break;

				case CustomAttributeDataType.Number:
					additionals.push({
						type: FilterBar2.TYPES.NUMBER_CONDITION,
						name: filterName,
						title: item.name,
						additional: true,
						applyFilterOnValueChange: true
					});
					break;

				case CustomAttributeDataType.Enum:
					additionals.push({
						type: FilterBar2.TYPES.SELECT,
						name: filterName,
						title: item.name,
						additional: true,
						multiselect: true,
						applyFilterOnValueChange: true,
						values: item.enum_definition.map(enumDef => ({
							label: String(enumDef.name),
							value: enumDef.value
						}))
					});
					break;

				default:
					break;
			}
		});
	}
	// and CA filters always be the last filters
	return { defaultList, additionals };
};
